import { GRAVITATIONAL_CONSTANT } from "@/lib/tool-kit/physics-fields/constants";
import {
  formatEcho,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-fields/format";
import type {
  OrbitKind,
  OrbitSolution,
} from "@/lib/tool-kit/physics-fields/orbit";

/** The equations for a circular orbit with this solution's numbers substituted, in SI. */
export function orbitWorking(
  kind: OrbitKind,
  mass: number,
  s: OrbitSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  const G = e(GRAVITATIONAL_CONSTANT);
  const M = e(mass);
  const lines: string[] = [];
  if (kind === "altitude") {
    lines.push(
      `r = R + h = ${e(s.distance - (s.altitude ?? 0))} + ${e(s.altitude ?? 0)} = ${r(s.distance, "m")}`
    );
  } else if (kind === "speed") {
    lines.push(
      `r = G M / v² = ${G} × ${M} / (${e(s.speed)})² = ${r(s.distance, "m")}`
    );
  } else if (kind === "period") {
    lines.push(
      `r = (G M T² / 4π²)^(1/3) = (${G} × ${M} × (${e(s.period)})² / 4π²)^(1/3) = ${r(s.distance, "m")}`
    );
  }
  lines.push(
    `v = √(G M / r) = √(${G} × ${M} / ${e(s.distance)}) = ${r(s.speed, "m/s")}`,
    `T = 2π r / v = 2π × ${e(s.distance)} / ${e(s.speed)} = ${r(s.period, "s")}`,
    `v_esc = √2 × v = 1.4142 × ${e(s.speed)} = ${r(s.escapeSpeed, "m/s")}`
  );
  return lines;
}
